<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import DataTable from '@/components/tables/DataTable.vue';
import DataTableColumn from '@/components/tables/DataTableColumn.vue';
import DataTableStatusBadge from '@/components/tables/DataTableStatusBadge.vue';
import { BuildingStatusEnum } from '@/enums/BuildingStatus.ts';
import { useDate } from '@/composables/useDate.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Building } from '@/typings/buildings/Building.ts';

const { getClosestAvailabilityDate } = useDate();

const { t } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const props = defineProps<{
  buildings: Building[];
  mode?: 'rental' | 'purchase';
}>();

const emit = defineEmits<{
  'building-click': [building: Building];
}>();

const hoveredBuilding = defineModel<Building | undefined>('hovered');

function onRowClick(building: Building) {
  emit('building-click', building);
}
</script>

<template>
  <DataTable v-model:hovered="hoveredBuilding"
             class="dataTable_buildings" 
             :items="buildings"
             key-field="id"
             @row-click="onRowClick">
    <DataTableColumn field="no"
                     :label="t('browsingApp.buildings.label')"
                     class="buildingNumberColumn">
      <template #body="{ value } : { value: string, item: Building }">
        {{ `${t('browsingApp.buildings.buildingNumber')} ${value}` }}
      </template>
    </DataTableColumn>
    <DataTableColumn field="localizedStreet"
                     :label="t('browsingApp.buildings.street')"
                     class="streetColumn" />
    <DataTableColumn field="closestAvailabilityDate"
                     :label="props.mode === 'purchase' ? t('browsingApp.buildings.closestAvailabilityDate') : t('unitSummary.moveInDate')"
                     class="availabilityDateColumn">
      <template #body="{ value, item } : { value: string | null, item: Building }">
        <div v-if="item.status !== BuildingStatusEnum.Full">
          {{ getClosestAvailabilityDate(value) }}
        </div>
        <div v-else>
          {{ t('common.notAvailableAbbreviated') }}
        </div>
      </template>
    </DataTableColumn>
    <DataTableColumn field="status"
                     :label="t('browsingApp.statuses.label')"
                     class="statusColumn">
      <template #body="{ value, item } : { value: BuildingStatusEnum, item: Building }">
        <div class="statusColumn_content">
          <DataTableStatusBadge class="statusBadge_building"
                                :status="value"
                                :label="t(`browsingApp.statuses.buildingStatus.${value}`)" />
          <div v-if="item.hasPromotion"
               class="tagPromotion">{{ t('unitSummary.promotion') }}</div>
        </div>
      </template>
    </DataTableColumn>
  </DataTable>
</template>

<style lang="scss">
@media screen and (min-width: 1400px){
  .dataTable_buildings {
    .dataTable-header,
    .dataTable-cell{
      width:25%;
    }
  }
}

.dataTable_buildings {
  .dataTable-header,
  .dataTable-cell{
    &.buildingNumberColumn{
      order:1
    }
    &.streetColumn{
      order:2
    }
    &.statusColumn{
      order:4;
    }
    &.availabilityDateColumn{
      order:3;
    }
  }
}

.statusColumn_content{
    display:flex;
    gap: 1.0rem;
    align-items: center;
    flex-wrap: wrap;
}

@media screen and (max-width: 1400px)  {
  .dataTable_buildings {
    .buildingNumberColumn {
      font-weight: 600;
    }
  }
}
</style>
